import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { ManageLocationDialogComponent } from '../locations/manage-location-dialog/manage-location-dialog.component';
import { ManageLocationDialogData, Location, LocationSelectorValue, LocationSuggestion } from 'src/app/models/location.model';
import { ApiService } from 'src/app/services/api.service';
import { GeolocationService, LocationPermissionError } from 'src/app/services/geolocation.service';
import { LocationAccessStatusEnum } from 'src/app/enums/location-access-status.enum';
import { LocationRelatableTypeEnum } from 'src/app/enums/location-relatable-type.enum';
import { GeoCoordinates } from 'src/app/interfaces/geo-coordinates.interface';
import { LocationDialogModeEnum } from 'src/app/enums/location-dialog-mode.enum';
import { EmployeeService } from 'src/app/services/employee.service';
import { PlatformService } from 'src/app/services/platform.service';

@Component({
  selector: 'app-location-selector',
  templateUrl: './location-selector.component.html',
  styleUrl: './location-selector.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButton, MatMenu, MatMenuItem, MatMenuTrigger, MatIcon, MatDivider],
})
export class LocationSelectorComponent implements OnInit {
  @Input({ required: true }) relatable_type: LocationRelatableTypeEnum;
  @Input({ required: true }) relatable_id: number;

  @Input() can_create_location = true;
  @Input() create_relatable_type?: LocationRelatableTypeEnum | null;
  @Input() create_relatable_id?: number | null;

  @Output() location_change = new EventEmitter<LocationSelectorValue>();
  @Output() icloud_private_relay_warning = new EventEmitter<boolean>();

  private api = inject(ApiService);
  private dialog = inject(MatDialog);
  private geolocation = inject(GeolocationService);
  private snack_bar = inject(MatSnackBar);
  private employee = inject(EmployeeService);
  private platform = inject(PlatformService);

  readonly options = signal<LocationSuggestion[]>([]);
  readonly locating = signal(false);

  selected_option: LocationSuggestion | null = null;
  private current_coords: GeoCoordinates | null = null;
  private location_permission_denied = false;

  get selected_label(): string {
    return this.selected_option?.name ?? 'Ubicación';
  }

  ngOnInit(): void {
    void this.loadOptions();
  }

  onSelectionChange(option: LocationSuggestion): void {
    this.selected_option = option;
    this.emitCurrentSelection();
  }

  retryLocation(): void {
    void this.loadOptions();
  }

  openCreateDialog(): void {
    if (!this.can_create_location) {
      return;
    }

    const has_locked_create_context = this.create_relatable_type !== undefined && this.create_relatable_id !== undefined;

    const data: ManageLocationDialogData = has_locked_create_context
      ? {
          mode: LocationDialogModeEnum.Create,
          relatable_type: this.create_relatable_type!,
          relatable_id: this.create_relatable_id!,
        }
      : { choose_relatable: true };

    this.dialog
      .open(ManageLocationDialogComponent, {
        data: data satisfies ManageLocationDialogData,
        width: '980px',
      })
      .afterClosed()
      .subscribe((location: Location | null | undefined) => {
        if (location?.id != null) {
          void this.loadOptions(location);
        }
      });
  }

  private async loadOptions(preferred?: number | Location): Promise<void> {
    this.locating.set(true);
    this.location_change.emit({
      location_ip_id: null,
      location_id: null,
      lat: null,
      lon: null,
      attach_current_ip: false,
      can_attach_current_ip: false,
      location_name: null,
      gps_available: false,
      location_permission_denied: false,
      locating: true,
    });

    const preferred_location = typeof preferred === 'object' && preferred != null ? preferred : null;
    const preferred_location_id = preferred_location?.id ?? (typeof preferred === 'number' ? preferred : undefined);

    try {
      this.current_coords = await this.resolveCoordinates();
      const params: Record<string, number | boolean> =
        this.current_coords != null ? { lat: this.current_coords.lat, lon: this.current_coords.lon } : {};

      const response = await firstValueFrom(this.api.getLocationSuggestions(params));
      let suggestions = response.data ?? [];
      const office_available = suggestions.some(suggestion => suggestion.is_office);
      this.icloud_private_relay_warning.emit(response.show_icloud_private_relay_warning === true && !office_available);

      if (preferred_location_id != null && !suggestions.some(suggestion => suggestion.id === preferred_location_id)) {
        const created =
          preferred_location != null ? this.toSuggestion(preferred_location) : await this.fetchPreferredAsSuggestion(preferred_location_id);

        if (created != null) {
          suggestions = [...suggestions, created];
        }
      }

      if (this.employee.isMeetingOngoing()) {
        const meeting_mode_company_product_id = await firstValueFrom(this.employee.meetingModeCompanyProduct);
        const meeting_mode_company_product = await firstValueFrom(this.api.getCompanyProduct(meeting_mode_company_product_id.toString()));
        suggestions = suggestions.filter(
          location =>
            location.relatable_type === LocationRelatableTypeEnum.Company &&
            location.relatable_id === meeting_mode_company_product.company_id
        );
      }

      this.options.set(suggestions);

      const preferred_option =
        preferred_location_id != null ? suggestions.find(suggestion => suggestion.id === preferred_location_id) : null;

      this.selected_option = preferred_option ?? suggestions[0] ?? null;
    } catch {
      this.icloud_private_relay_warning.emit(false);
    } finally {
      this.locating.set(false);
      this.emitCurrentSelection();
    }
  }

  private async resolveCoordinates(): Promise<GeoCoordinates | null> {
    try {
      this.location_permission_denied = false;
      return await this.geolocation.getCurrentPosition();
    } catch (error) {
      const permission_denied = error instanceof LocationPermissionError && error.status === LocationAccessStatusEnum.Denied;
      this.location_permission_denied = permission_denied;

      const snack_bar_ref = this.snack_bar.open(
        permission_denied
          ? 'Activa Bisual Intranet en Ajustes > Privacidad y seguridad > Localización. Mientras tanto, se usará solo la IP.'
          : 'No se ha podido obtener tu ubicación GPS. Se usará solo la IP.',
        permission_denied && this.platform.isElectronApp() ? 'Abrir ajustes' : undefined,
        {
          duration: 5_000,
        }
      );

      snack_bar_ref.onAction().subscribe(() => {
        void this.platform.openLocationSettings();
      });

      return null;
    }
  }

  private canAttachCurrentIp(option: LocationSuggestion): boolean {
    return option.matched_by_coordinates && !option.matched_by_ip;
  }

  private emitCurrentSelection(): void {
    const option = this.selected_option;
    const lat = this.current_coords?.lat ?? null;
    const lon = this.current_coords?.lon ?? null;
    const gps_flags = {
      gps_available: lat != null && lon != null,
      location_permission_denied: this.location_permission_denied,
      locating: this.locating(),
    };

    if (option == null) {
      this.location_change.emit({
        location_ip_id: null,
        location_id: null,
        lat,
        lon,
        attach_current_ip: false,
        can_attach_current_ip: false,
        location_name: null,
        ...gps_flags,
      });
      return;
    }

    const can_attach_current_ip = option.location_ip_id == null && this.canAttachCurrentIp(option);

    this.location_change.emit({
      location_ip_id: option.location_ip_id,
      location_id: option.id,
      lat,
      lon,
      attach_current_ip: false,
      can_attach_current_ip,
      location_name: option.name,
      ...gps_flags,
    });
  }

  private async fetchPreferredAsSuggestion(location_id: number): Promise<LocationSuggestion | null> {
    const response = await firstValueFrom(
      this.api.getLocations({
        relatable_type: this.relatable_type,
        relatable_id: this.relatable_id,
      })
    );

    const location = (response.data ?? []).find(item => item.id === location_id);

    if (location == null) {
      return null;
    }

    return {
      ...location,
      matched_by_ip: false,
      matched_by_coordinates: false,
      location_ip_id: null,
    };
  }

  private toSuggestion(location: Location): LocationSuggestion {
    return {
      ...location,
      matched_by_ip: false,
      matched_by_coordinates: false,
      location_ip_id: null,
    };
  }
}
